import { useCallback, useEffect, useState } from 'react';
import { Sparkles, UserRound, X, Pause } from 'lucide-react';
import { toast } from 'sonner';
import { AiAgentLog } from '../../../types';
import { isManagerRole } from '../../../lib/training';
import {
    AiLeadState, fetchAiLeadState, setAiEnabled, reactivateAi, fetchLatestHandoff,
    fetchLatestSuggestion, reviewSuggestion, enrollLeadsForReactivation, subscribeToAiSuggestions,
} from '../../../services/aiAgentService';

// Sem realtime a tela ainda se atualiza: busca de novo a cada POLL_MS enquanto o chat está aberto.
const POLL_MS = 15000;

interface AiSuggestionBarProps {
    leadId: number;
    userId: string;
    userRole?: string | null;
    /** Horário (ms) da última mensagem enviada neste chat: sugestão mais antiga que isso já foi superada. */
    lastOutboundAt: number | null;
    /** (Modo copiloto) o vendedor escolheu usar a sugestão: o texto vai para a caixa de envio, ainda editável. */
    onUse: (text: string, suggestionId: string) => void;
}

const HANDOFF_REASONS: Record<string, string> = {
    pediu_desconto: 'o lead pediu desconto ou condição especial',
    pediu_pessoa: 'o lead pediu para falar com uma pessoa',
    reclamacao: 'reclamação ou irritação do lead',
    pagamento: 'problema de pagamento',
    fora_da_base: 'dúvida fora da base aprovada ou resposta bloqueada pelas regras',
    objecao_repetida: 'objeção repetida sem avanço',
    limite_diario: 'limite diário de respostas atingido (confira se há outro robô na conversa)',
};

function describeHandoff(s: AiAgentLog): string {
    const reason = s.reason || '';
    if (HANDOFF_REASONS[reason]) return HANDOFF_REASONS[reason];
    if (reason.startsWith('parar')) return 'o lead pediu para parar de receber mensagens';
    return reason || 'a IA não teve confiança para responder';
}

const isFuture = (iso?: string | null) => !!iso && new Date(iso).getTime() > Date.now();

export function AiSuggestionBar({ leadId, userId, userRole, lastOutboundAt, onUse }: AiSuggestionBarProps) {
    const [aiState, setAiState] = useState<AiLeadState | null>(null);
    const [suggestion, setSuggestion] = useState<AiAgentLog | null>(null);
    const [handoff, setHandoff] = useState<AiAgentLog | null>(null);
    const [busy, setBusy] = useState(false);
    const canToggle = isManagerRole(userRole);

    const refresh = useCallback(async () => {
        const [state, latest, lastHandoff] = await Promise.all([
            fetchAiLeadState(leadId), fetchLatestSuggestion(leadId), fetchLatestHandoff(leadId),
        ]);
        setAiState(state);
        setSuggestion(latest);
        setHandoff(lastHandoff);
    }, [leadId]);

    useEffect(() => {
        refresh();
        const unsubscribe = subscribeToAiSuggestions(leadId, refresh);
        const timer = setInterval(refresh, POLL_MS);
        return () => { unsubscribe(); clearInterval(timer); };
    }, [leadId, refresh]);

    const handleToggle = async () => {
        if (!aiState || busy) return;
        setBusy(true);
        try {
            await setAiEnabled(leadId, !aiState.enabled);
            toast.success(aiState.enabled ? 'Atendente de IA desligado para este lead.' : 'Atendente de IA ligado: ele responde este lead sozinho.');
            await refresh();
        } catch (e) {
            console.error('Erro ao alterar o atendente de IA:', e);
            toast.error('Não foi possível alterar o atendente de IA.');
        } finally {
            setBusy(false);
        }
    };

    const handleReactivate = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await reactivateAi(leadId);
            toast.success('Atendente de IA reativado: ele volta a responder este lead.');
            await refresh();
        } catch (e) {
            console.error('Erro ao reativar o atendente de IA:', e);
            toast.error('Não foi possível reativar o atendente de IA.');
        } finally {
            setBusy(false);
        }
    };

    const handleStartReactivation = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const n = await enrollLeadsForReactivation([leadId]);
            if (n > 0) toast.success('Lead direcionado: a IA fará o primeiro contato em breve.');
            else toast.error('Este lead não pode ser direcionado (pediu para parar de receber mensagens).');
            await refresh();
        } catch (e) {
            console.error('Erro ao direcionar o lead para a IA:', e);
            toast.error('Não foi possível direcionar o lead para a IA.');
        } finally {
            setBusy(false);
        }
    };

    const dismiss = async () => {
        if (!suggestion) return;
        const current = suggestion;
        setSuggestion(null);
        try {
            await reviewSuggestion(current.id, 'rejected', null, userId);
        } catch (e) {
            console.error('Erro ao registrar a revisão da sugestão:', e);
        }
    };

    const enabled = !!aiState?.enabled;
    const paused = enabled && isFuture(aiState?.pausedUntil);

    // Sugestões só existem no modo copiloto; sugestão mais antiga que a última mensagem enviada já foi superada.
    const visibleSuggestion = suggestion && aiState?.mode === 'copilot'
        && (!lastOutboundAt || new Date(suggestion.created_at).getTime() > lastOutboundAt) ? suggestion : null;

    if (!canToggle && !enabled) return null;

    return (
        <div className="mb-3 space-y-2">
            {aiState && (canToggle || enabled) && (
                <div className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-violet-50/70 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-900/30">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                        {paused ? <Pause size={14} /> : <Sparkles size={14} />}
                        <span>
                            {!enabled ? 'Atendente de IA · desligado'
                                : paused ? 'Atendente de IA · em pausa'
                                : aiState.mode === 'copilot' ? 'Atendente de IA · sugere respostas'
                                : 'Atendente de IA · atendendo sozinho'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        {paused && (
                            <button
                                type="button"
                                disabled={busy}
                                onClick={handleReactivate}
                                className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                            >
                                Reativar IA
                            </button>
                        )}
                        {canToggle && !enabled && (
                            <button
                                type="button"
                                disabled={busy}
                                onClick={handleStartReactivation}
                                className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
                            >
                                Iniciar reativação
                            </button>
                        )}
                        {canToggle && (
                            <button
                                type="button"
                                role="switch"
                                aria-checked={enabled}
                                aria-label="Atendente de IA neste lead"
                                disabled={busy}
                                onClick={handleToggle}
                                className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-60 ${enabled ? 'bg-violet-600' : 'bg-slate-300 dark:bg-slate-600'}`}
                            >
                                <span className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-4' : ''}`} />
                            </button>
                        )}
                    </div>
                </div>
            )}

            {paused && handoff && (
                <div className="px-3 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-200">
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-1">
                        <UserRound size={13} /> A IA passou este lead para você
                    </span>
                    <p className="text-sm">Motivo: {describeHandoff(handoff)}.</p>
                    {handoff.message && <p className="mt-1 text-xs opacity-90 whitespace-pre-wrap">{handoff.message}</p>}
                </div>
            )}

            {visibleSuggestion?.decision === 'reply' && visibleSuggestion.message && (
                <div className="px-3 py-2.5 rounded-xl border border-violet-200 bg-violet-50 text-violet-950 dark:border-violet-800/50 dark:bg-violet-900/20 dark:text-violet-100">
                    <div className="flex items-center justify-between mb-1">
                        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
                            <Sparkles size={13} /> Sugestão da IA · revise antes de enviar
                        </span>
                        <button type="button" aria-label="Descartar sugestão" onClick={dismiss} className="p-1 rounded-full text-violet-500 hover:bg-violet-100 dark:hover:bg-violet-900/40">
                            <X size={14} />
                        </button>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{visibleSuggestion.message}</p>
                    <div className="mt-2 flex gap-2">
                        <button
                            type="button"
                            onClick={() => onUse(visibleSuggestion.message as string, visibleSuggestion.id)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700"
                        >
                            Usar sugestão
                        </button>
                        <button type="button" onClick={dismiss} className="px-3 py-1.5 text-xs font-semibold rounded-lg text-violet-700 hover:bg-violet-100 dark:text-violet-200 dark:hover:bg-violet-900/40">
                            Descartar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
